import * as React from 'react';

/**
 * Параметры хука, который позволяет монтировать и демонтировать содержимое Drawer-а
 */
type UseMountingParams = {
  /**
   * Извне Drawer контролируется обычным opened и onClose — это и есть outerOpened и onClose
   */
  outerOpened: boolean;
  onClose: VoidFunction;
};

/**
 * Результат работы хука — флаги, которые позволяют монтировать или демонтировать Drawer
 */
type UseMountingResult = {
  /**
   * Если mounted — true, монтируем drawer, иначе нет
   */
  mounted: boolean;
  /**
   * Если finalOpened — true, устанавливаем drawer-у класс модификатор-opened,
   * чтобы он через transition поднялся вверх
   */
  finalOpened: boolean;
  /**
   * Callback надо вызывать, когда drawer через transition поднимется вверх или опустится вниз
   */
  handleTransitionEnd: VoidFunction;
  /**
   * Callback надо вызывать, когда пользователь пытается закрыть Drawer на крестик или по клику на фон
   */
  handleClose: VoidFunction;
};

/**
 * Хук позволяет демонтировать drawer после его закрытия и монтировать перед открытием с анимацией
 * @param outerOpened — извне передается opened: boolean
 * @param onClose — callback извне
 */
export const useMounting = ({
  outerOpened,
  onClose,
}: UseMountingParams): UseMountingResult => {
  const [finalOpened, setFinalOpened] = React.useState(outerOpened);

  const [mounted, setMounted] = React.useState(outerOpened);

  React.useEffect(() => {
    if (outerOpened) {
      setMounted(true);
    }

    if (!outerOpened) {
      setFinalOpened(false);
    }
  }, [outerOpened]);

  React.useEffect(() => {
    setFinalOpened(mounted);
  }, [mounted]);

  const handleTransitionEnd = React.useCallback(() => {
    if (!finalOpened) {
      setMounted(false);
    }
  }, [finalOpened]);

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  return {
    mounted,
    finalOpened,
    handleTransitionEnd,
    handleClose,
  };
};
